import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Paper,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  ContentCut as ServiceIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  Check as CheckIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useServices } from '../../hooks/queries';
import { useDialog } from '../../hooks/useDialog';
import { useSnackbar } from '../../hooks/useSnackbar';
import { useQueryClient } from '@tanstack/react-query';
import { formatCurrency, formatDuration } from '../../utils/formatters';
import { SERVICE_DEFAULTS } from '../../utils/constants';
import ServiceForm from '../forms/ServiceForm';
import CascadeDeleteDialog from '../ui/CascadeDeleteDialog';
import AppSnackbar from '../ui/AppSnackbar';

/**
 * ServicesManagerDialog
 *
 * Componente de diálogo reutilizável que gerencia o catálogo de serviços.
 * Replica 100% da funcionalidade da página de serviços em forma modal.
 *
 * @param {boolean} open - Se o dialog está aberto
 * @param {function} onClose - Callback ao fechar o dialog
 * @param {string} title - Título do dialog (opcional - padrão: "Gerenciar Serviços")
 *
 * @example
 * const [open, setOpen] = useState(false);
 * <ServicesManagerDialog
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   title="Gerenciar Serviços"
 * />
 */
export default function ServicesManagerDialog({
  open,
  onClose,
  title = 'Gerenciar Serviços'
}) {
  const { items: services, loading, createAsync, updateAsync, reload } = useServices();
  const queryClient = useQueryClient();
  const editDialog = useDialog();
  const deleteDialog = useDialog();
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Estados de filtro e ordenação
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('name-asc');
  const [quickCreateMode, setQuickCreateMode] = useState(false);
  const [quickCreateName, setQuickCreateName] = useState('');

  // Limpeza ao fechar/abrir
  useEffect(() => {
    if (!open) {
      editDialog.handleClose();
      deleteDialog.handleClose();
      setSearchText('');
      setSortBy('name-asc');
      setQuickCreateMode(false);
      setQuickCreateName('');
    }
  }, [open]);

  // Filtro e ordenação front-side
  const filteredAndSorted = useMemo(() => {
    let result = [...(services || [])];

    // Filtro por texto
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(s =>
        s.name?.toLowerCase().includes(lower) ||
        s.description?.toLowerCase().includes(lower) ||
        s.category?.toLowerCase().includes(lower)
      );
    }

    // Ordenação
    const sortFns = {
      'name-asc': (a, b) => (a.name || '').localeCompare(b.name || ''),
      'name-desc': (a, b) => (b.name || '').localeCompare(a.name || ''),
      'price-asc': (a, b) => (a.defaultPrice || 0) - (b.defaultPrice || 0),
      'price-desc': (a, b) => (b.defaultPrice || 0) - (a.defaultPrice || 0),
      'duration-asc': (a, b) => (a.defaultDurationMinutes || 0) - (b.defaultDurationMinutes || 0),
      'duration-desc': (a, b) => (b.defaultDurationMinutes || 0) - (a.defaultDurationMinutes || 0),
    };
    result.sort(sortFns[sortBy] || sortFns['name-asc']);

    return result;
  }, [services, searchText, sortBy]);

  // Handlers de edição
  const handleOpenEditDialog = (service = null) => {
    editDialog.handleOpen(service);
  };

  const handleCloseEditDialog = () => {
    editDialog.handleClose();
  };

  const handleSubmit = async (formData) => {
    try {
      if (editDialog.isEdit) {
        await updateAsync({ id: editDialog.data.id, data: formData });
        showSuccess('Serviço atualizado com sucesso!');
      } else {
        await createAsync(formData);
        showSuccess('Serviço criado com sucesso!');
      }
      // Invalidar caches relacionados
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['places'] });
      handleCloseEditDialog();
    } catch (error) {
      showError('Erro ao salvar: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handlers de exclusão
  const handleOpenDeleteDialog = (service) => {
    deleteDialog.handleOpen(service);
  };

  const handleCloseDeleteDialog = () => {
    deleteDialog.handleClose();
  };

  // Criação rápida
  const handleQuickCreateSubmit = async () => {
    if (!quickCreateName.trim()) {
      showError('Nome do serviço é obrigatório');
      return;
    }

    try {
      await createAsync({
        name: quickCreateName.trim(),
        defaultDurationMinutes: SERVICE_DEFAULTS.DURATION_MINUTES,
        defaultPrice: SERVICE_DEFAULTS.DEFAULT_PRICE,
      });
      showSuccess('Serviço criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['services'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['places'] });
      setQuickCreateMode(false);
      setQuickCreateName('');
    } catch (error) {
      showError('Erro ao criar serviço: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '500px' } }}
      >
        {/* Dialog Title com fechar X */}
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <ServiceIcon color="primary" />
          <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Tooltip title="Fechar">
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent dividers>
          {/* Barra de ferramentas: busca, ordenação, novo serviço */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Campo de busca */}
            <TextField
              placeholder="Buscar serviço..."
              variant="outlined"
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.disabled' }} />,
              }}
              sx={{ minWidth: '250px', flexGrow: 1 }}
            />

            {/* Ordenação */}
            <TextField
              select
              variant="outlined"
              size="small"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              sx={{ minWidth: '200px' }}
            >
              <MenuItem value="name-asc">Nome A-Z</MenuItem>
              <MenuItem value="name-desc">Nome Z-A</MenuItem>
              <MenuItem value="price-asc">Preço Menor</MenuItem>
              <MenuItem value="price-desc">Preço Maior</MenuItem>
              <MenuItem value="duration-asc">Duração Menor</MenuItem>
              <MenuItem value="duration-desc">Duração Maior</MenuItem>
            </TextField>

            {/* Botão Novo Serviço */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setQuickCreateMode(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Novo Serviço
            </Button>
          </Box>

          {/* Modo de criação rápida */}
          {quickCreateMode && (
            <Paper
              elevation={3}
              sx={{
                p: 2,
                mb: 3,
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'primary.main',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  autoFocus
                  label="Nome do serviço"
                  placeholder="Digite o nome..."
                  variant="outlined"
                  size="small"
                  value={quickCreateName}
                  onChange={(e) => setQuickCreateName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleQuickCreateSubmit();
                    }
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Confirmar">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleQuickCreateSubmit}
                    >
                      <CheckIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancelar">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setQuickCreateMode(false);
                        setQuickCreateName('');
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Deixe em branco para editar campos adicionais (duração, preço, categoria) após criar.
              </Typography>
            </Paper>
          )}

          {/* Estado de carregamento */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredAndSorted.length === 0 ? (
            // Estado vazio
            <Paper
              elevation={0}
              sx={{
                p: 6,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: 'divider',
              }}
            >
              <ServiceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Nenhum serviço encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {searchText.trim() ? 'Tente uma busca diferente ou crie um novo serviço.' : 'Crie seu primeiro serviço para começar.'}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSearchText('');
                  setQuickCreateMode(true);
                }}
              >
                Criar Primeiro Serviço
              </Button>
            </Paper>
          ) : (
            // Lista de serviços
            <List
              disablePadding
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {filteredAndSorted.map((service, index) => (
                <Box key={service.id}>
                  <ListItem
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Editar">
                          <IconButton
                            edge="end"
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(service)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(service)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="600">
                          {service.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                          {service.category && (
                            <Chip
                              label={service.category}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem' }}
                            />
                          )}
                          {service.defaultDurationMinutes != null && (
                            <Chip
                              icon={<TimeIcon sx={{ fontSize: '0.875rem' }} />}
                              label={formatDuration(service.defaultDurationMinutes)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                            />
                          )}
                          {service.defaultPrice != null && (
                            <Chip
                              icon={<MoneyIcon sx={{ fontSize: '0.875rem' }} />}
                              label={formatCurrency(service.defaultPrice)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: '0.7rem', '& .MuiChip-icon': { ml: 0.5 } }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < filteredAndSorted.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          )}
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose}>
            Fechar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSearchText('');
              setQuickCreateMode(true);
            }}
          >
            Novo Serviço
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Criar/Editar - aninhado */}
      <ServiceForm
        open={editDialog.open}
        onClose={handleCloseEditDialog}
        onSubmit={handleSubmit}
        data={editDialog.data}
      />

      {/* Dialog de Confirmação de Exclusão - aninhado */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        onDeleted={() => {
          handleCloseDeleteDialog();
          showSuccess('Serviço excluído com sucesso!');
          queryClient.invalidateQueries({ queryKey: ['services'] });
          queryClient.invalidateQueries({ queryKey: ['schedules'] });
          queryClient.invalidateQueries({ queryKey: ['places'] });
          reload();
        }}
        deletePath={`/services/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} autoHideDuration={6000} />
    </>
  );
}
